/**
 * Color tokens — extracted from Figma via REST API (file
 * n0JrTQocVPy7btXCG4YxwC, landing frame 58:39161).
 *
 * Sources:
 *   primary / logo   → #0410FF (logo-mark SVG fill + Round Button)
 *   navy (deep)      → #00243E (Madlib fill)
 *   navy (text)      → #04233C (primary text color, verified via inspector)
 *   coral            → #FF4F29 (Discover Round Button)
 *   mint             → #C3F3FF (Resources section fill)
 */
import type { PaletteOptions } from '@mui/material';

declare module '@mui/material/styles' {
  interface Palette {
    brand: BrandPalette;
    surface: SurfacePalette;
  }
  interface PaletteOptions {
    brand?: BrandPalette;
    surface?: SurfacePalette;
  }
}

export interface BrandPalette {
  royalBlue: string;
  navy: { primary: string; deep: string; muted: string };
  coral: string;
  mint: string;
}

export interface SurfacePalette {
  page: string;
  card: string;
  onDark: string;
  onMint: string;
  divider: string;
  placeholder: string;
  footerText: string;
  mapLand: string;
  mapWater: string;
  legalText: string;
  dataValue: string;
  linkBlue: string;
}

const raw = {
  royalBlue: '#0410FF',
  // Primary text color — Figma "Primary-text" #04233C (verified via direct
  // inspector reading; the old #00243E was a slightly stale first-pass
  // extraction).
  navyPrimary: '#04233C',
  navyDeep: '#00243E',
  navyMuted: '#2C3A61',
  coral: '#FF4F29',
  mint: '#C3F3FF',
  white: '#FFFFFF',
  slateDivider: '#E4E7EE',
  // Search placeholder — Figma #889099
  slatePlaceholder: '#889099',
  // Footer legal text — Figma Text/text-secondary (700) #414651
  slateFooterText: '#414651',
  // Impact map — unselected state fill (Figma #FAFAF8) and water (#E3F3F8)
  mapLand: '#FAFAF8',
  mapWater: '#E3F3F8',
  // Legal disclaimer text — Figma "Text/text-tertiary (600)" #535862
  legalText: '#535862',
  // Overview Panel metric values / opportunity score — Figma "Data" text
  // nodes, a distinct blue from primary.main (#0410FF).
  dataValue: '#202599',
  // "Discover our full resource list" link — a distinct, brighter blue from
  // primary.main, verified via inspector (also matches the ticker's
  // hourglass icon fill).
  linkBlue: '#0611F6',
} as const;

const brand: BrandPalette = {
  royalBlue: raw.royalBlue,
  navy: {
    primary: raw.navyPrimary,
    deep: raw.navyDeep,
    muted: raw.navyMuted,
  },
  coral: raw.coral,
  mint: raw.mint,
};

const surface: SurfacePalette = {
  page: raw.white,
  card: raw.white,
  onDark: raw.navyDeep,
  onMint: raw.mint,
  divider: raw.slateDivider,
  placeholder: raw.slatePlaceholder,
  footerText: raw.slateFooterText,
  mapLand: raw.mapLand,
  mapWater: raw.mapWater,
  legalText: raw.legalText,
  dataValue: raw.dataValue,
  linkBlue: raw.linkBlue,
};

export const paletteOptions: PaletteOptions = {
  mode: 'light',
  primary: {
    main: brand.royalBlue,
    contrastText: raw.white,
  },
  secondary: {
    main: brand.coral,
    contrastText: raw.white,
  },
  background: {
    default: surface.page,
    paper: surface.card,
  },
  text: {
    primary: brand.navy.primary,
    secondary: brand.navy.muted,
    disabled: surface.placeholder,
  },
  divider: surface.divider,
  common: {
    black: brand.navy.primary,
    white: raw.white,
  },
  brand,
  surface,
};
